import landingRedirects from './landing-redirects.json';
import exceptionsEnUs from './landing-redirects-exceptions-en-us.json';

const esc = (literal: string) => literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * Escape all RegExp metacharacters in a literal string.
 */
const pattern = new RegExp(
  Object.keys(landingRedirects)
    .sort((a, b) => b.length - a.length) // longest first
    .map(esc) // escape meta-chars
    .join('|'), // alternation
  'g' // global search
);

// SEO team requested to disabled en-US links for which they do no have redirects
const stripUnsupportedEnUSLinks = (html: string) => {
  const exceptionCheck = (href: string) =>
    exceptionsEnUs?.find((ex) => href.toLowerCase().includes(ex.toLowerCase()));

  // One pass over the HTML string
  return html.replace(
    /<a\b[^>]*\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>([\s\S]*?)<\/a>/gi,
    (fullMatch, hrefDouble, hrefSingle, innerHtml) => {
      const href = hrefDouble || hrefSingle || '';

      // Skip <a> tags that don't target /en-US/
      if (
        !href.includes('/en-US/') ||
        href?.includes('/en-US/subscribe') ||
        exceptionCheck(href)
      )
        return fullMatch;

      return innerHtml;
    }
  );
};

const REDIRECT_HOP_LIMIT = 10;

/**
 * Resolves one locale-less route to the path it finally serves.
 *
 * `replaceLandingRedirects` is for HTML: it substring-matches every key across
 * a document and also strips unsupported en-US anchors. A programmatically
 * built link needs neither, and substring matching on a bare route is unsafe —
 * so this does an exact-key lookup instead.
 *
 * Callers pass a route with no leading slash and no locale prefix, matching the
 * keys in `landing-redirects.json` (`auto/abo/auto-abo-unter-200-euro`).
 * Unknown routes come back unchanged, so this is safe to apply unconditionally.
 *
 * The map is flat today and a test keeps it that way, but the hop loop means a
 * chain introduced later still resolves rather than silently emitting a 301.
 */
export const resolveLandingRedirect = (route: string): string => {
  if (!route) {
    return route;
  }

  const redirects = landingRedirects as Record<string, string>;
  const seen = new Set([route]);
  let current = route;

  for (let hop = 0; hop < REDIRECT_HOP_LIMIT; hop += 1) {
    const next = redirects[current];

    if (!next || next === current || seen.has(next)) {
      return current;
    }

    seen.add(next);
    current = next;
  }

  return current;
};

export const replaceLandingRedirects = (txt: string): string => {
  if (!txt) {
    return txt;
  }
  // Whitelist exceptions for specific experience slugs (skip any rewriting)
  const experienceSlugs = [
    'experience-uniqbit',
    'experience-egym',
    'experience-kenbi',
    'experience-dcs',
  ];
  const containsExperienceSlug = (s: string) =>
    experienceSlugs.some((slug) => s.includes(slug));

  // If it's a plain URL string (not HTML), short-circuit for these slugs
  const isProbablyHtml = /<[^>]+>/.test(txt);
  if (!isProbablyHtml && containsExperienceSlug(txt)) {
    return txt;
  }
  const chunks = txt.split(/(<iframe\b[\s\S]*?<\/iframe>)/gi);

  // avoiding touching iframes
  for (let i = 0; i < chunks.length; i += 2) {
    chunks[i] = chunks[i].replace(pattern, (matched) => {
      return (landingRedirects as { [key: string]: string })[matched];
    });
  }

  // After generic replacements, restore guide paths for the experience slugs inside HTML
  let withExceptionsApplied = chunks.join('');
  experienceSlugs.forEach((slug) => {
    withExceptionsApplied = withExceptionsApplied
      .replaceAll(`/auto/${slug}`, `/guide/${slug}`)
      .replaceAll(`de-DE/auto/${slug}`, `de-DE/guide/${slug}`)
      .replaceAll(
        `https://www.finn.com/de-DE/auto/${slug}`,
        `https://www.finn.com/de-DE/guide/${slug}`
      );
  });

  return stripUnsupportedEnUSLinks(withExceptionsApplied);
};
